
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* =========================================================
   OPPIES ADMIN PORTAL
   STORES MANAGER

   Approved secondhand Stores only.

   Functions:
   - getAllStores
   - updateStoreDetails
   - deleteStore

   Cosmos:
   - Database: oppiesdb
   - Container: stores
   - Partition key: /email
   ========================================================= */

const API_BASE =
    "https://oppiesfunc.azurewebsites.net/api";

const GET_ALL_STORES_API =
    `${API_BASE}/getAllStores?code=KBfxDkDBLTTvOxMxkXQFzbNyGeZJGg3PLAGKGbTnPkKvAzFuzCtYxw==`;

const UPDATE_STORE_API =
    `${API_BASE}/updateStoreDetails?code=fBYbg24KXUZ83cWFzjDlAoSIOF7-y-UCxYUf5tzJEXbxAzFuJdI8eQ==`;

const DELETE_STORE_API =
    `${API_BASE}/deleteStore?code=7tkrhrtum8fZ2mtMZzAAdN1avLra3viMWUgfLIQ65eZ0AzFuqpiA7g==`;

// Temporary integration configuration.
// Replace with authenticated admin API access.
// If your current endpoints require function keys,
// configure them through your existing admin API
// integration rather than publishing secrets here.

const OPPIES_BEIGE = "#bfa97c";

const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const EDITABLE_FIELDS = [
    "name",
    "contact",
    "hours",
    "itemsOffered",
    "imageURL",
];

/* =========================================================
   HELPERS
   ========================================================= */

function text(value) {
    return String(value ?? "").trim();
}

function getStoreKey(store) {
    return text(store?.email || store?.id || store?.storeId);
}

function isApproved(store) {
    return (
        store?.approved === true ||
        (
            store?.approved === undefined &&
            store?.pendingApproval === false
        )
    );
}

function cloneStore(store) {
    return {
        ...store,
        hours: store?.hours
            ? { ...store.hours }
            : {},
        itemsOffered: Array.isArray(store?.itemsOffered)
            ? [...store.itemsOffered]
            : [],
    };
}

function getBillingStatus(store) {
    const status = text(
        store?.billing?.status ||
        store?.paymentStatus ||
        store?.subscriptionStatus
    ).toLowerCase();

    if (status) return status;

    if (store?.isSubscribed === true) {
        return "active";
    }

    return "not subscribed";
}

function formatBillingStatus(status) {
    const labels = {
        active: "Active",
        trialing: "Trial",
        past_due: "Past Due",
        unpaid: "Unpaid",
        canceled: "Cancelled",
        cancelled: "Cancelled",
        incomplete: "Incomplete",
        incomplete_expired: "Incomplete Expired",
        expired: "Expired",
        "not subscribed": "Not Subscribed",
    };

    return (
        labels[status] ||
        status.replace(/_/g, " ")
    );
}

function getBillingColor(status) {
    if (status === "active") {
        return {
            background: "#e6f5e9",
            color: "#256d38",
            border: "#b9e3c2",
        };
    }

    if (status === "trialing") {
        return {
            background: "#eaf3ff",
            color: "#245e9b",
            border: "#c5dcf6",
        };
    }

    if (
        status === "past_due" ||
        status === "unpaid" ||
        status === "expired"
    ) {
        return {
            background: "#fff0ee",
            color: "#a53f35",
            border: "#f0c7c1",
        };
    }

    return {
        background: "#f5f0e1",
        color: "#6b5b3a",
        border: "#d8c8a4",
    };
}

function formatDate(value) {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return text(value);
    }

    return date.toLocaleDateString("en-NZ", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

function formatLocation(store) {
    const lat =
        store?.location?.lat ??
        store?.location?.latitude ??
        store?.locationGeo?.coordinates?.[1];

    const lng =
        store?.location?.lng ??
        store?.location?.longitude ??
        store?.locationGeo?.coordinates?.[0];

    if (lat == null || lng == null) {
        return "Not set";
    }

    return `${lat}, ${lng}`;
}

function getErrorMessage(error, fallback) {
    const body = error?.response?.data;

    return (
        body?.error ||
        body?.message ||
        error?.message ||
        fallback
    );
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */

export default function StoresManager() {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);

    const [status, setStatus] = useState({
        message: "",
        isError: false,
    });

    const [search, setSearch] = useState("");

    const [expandedId, setExpandedId] = useState(null);
    const [editId, setEditId] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [drafts, setDrafts] = useState({});

    /* =====================================================
       LOAD STORES
       ===================================================== */

    const fetchStores = async () => {
        setLoading(true);

        setStatus({
            message: "",
            isError: false,
        });

        try {
            const response = await axios.get(
                GET_ALL_STORES_API,
                {
                    timeout: 30000,
                }
            );

            // getAllStores returns a direct array.
            const list = Array.isArray(response.data)
                ? response.data
                : [];

            // Extra safety: exclude pending approvals.
            const approvedStores = list.filter(isApproved);

            approvedStores.sort((a, b) =>
                text(a?.name).localeCompare(text(b?.name))
            );

            setStores(approvedStores);

        } catch (error) {
            console.error(
                "Failed to load Stores:",
                error
            );

            setStatus({
                message: getErrorMessage(
                    error,
                    "Failed to load Stores."
                ),
                isError: true,
            });

        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    /* =====================================================
       SEARCH
       ===================================================== */

    const filteredStores = useMemo(() => {
        const term = search.trim().toLowerCase();

        if (!term) return stores;

        return stores.filter((store) => {
            const haystack = [
                store?.name,
                store?.storeId,
                store?.id,
                store?.uid,
                store?.email,
                store?.address,
                store?.addressFormatted,
                store?.contact,
                store?.country,
            ]
                .map(text)
                .join(" ")
                .toLowerCase();

            return haystack.includes(term);
        });
    }, [stores, search]);

    const storeCounts = useMemo(() => {
        return {
            total: stores.length,
            active: stores.filter(
                (store) => getBillingStatus(store) === "active"
            ).length,
            trial: stores.filter(
                (store) => getBillingStatus(store) === "trialing"
            ).length,
        };
    }, [stores]);

    /* =====================================================
       EXPAND / EDIT
       ===================================================== */

    const toggleExpand = (storeKey) => {
        if (busyId) return;

        setStatus({
            message: "",
            isError: false,
        });

        if (expandedId === storeKey) {
            setExpandedId(null);
            setEditId(null);
            return;
        }

        setExpandedId(storeKey);
        setEditId(null);

        const store = stores.find(
            (item) => getStoreKey(item) === storeKey
        );

        if (store) {
            setDrafts((previous) => ({
                ...previous,
                [storeKey]: cloneStore(store),
            }));
        }
    };

    const startEdit = (storeKey) => {
        const store = stores.find(
            (item) => getStoreKey(item) === storeKey
        );

        if (!store) return;

        setStatus({
            message: "",
            isError: false,
        });

        setDrafts((previous) => ({
            ...previous,
            [storeKey]: cloneStore(store),
        }));

        setEditId(storeKey);
    };

    const cancelEdit = (storeKey) => {
        const store = stores.find(
            (item) => getStoreKey(item) === storeKey
        );

        if (store) {
            setDrafts((previous) => ({
                ...previous,
                [storeKey]: cloneStore(store),
            }));
        }

        setEditId(null);
    };

    /* =====================================================
       DRAFT CHANGES
       ===================================================== */

    const updateDraftField = (
        storeKey,
        field,
        value
    ) => {
        setDrafts((previous) => ({
            ...previous,
            [storeKey]: {
                ...(previous[storeKey] || {}),
                [field]: value,
            },
        }));
    };

    const updateDraftHour = (
        storeKey,
        day,
        value
    ) => {
        setDrafts((previous) => ({
            ...previous,
            [storeKey]: {
                ...(previous[storeKey] || {}),
                hours: {
                    ...(
                        previous[storeKey]?.hours || {}
                    ),
                    [day]: value,
                },
            },
        }));
    };

    /* =====================================================
       SAVE STORE
       ===================================================== */

    const handleSave = async (storeKey) => {
        const draft = drafts[storeKey];

        const original = stores.find(
            (item) => getStoreKey(item) === storeKey
        );

        if (!draft || !original) return;

        if (!text(draft.name)) {
            setStatus({
                message: "Store name cannot be empty.",
                isError: true,
            });
            return;
        }

        if (!original.email) {
            setStatus({
                message:
                    "This Store has no email partition key. Cannot save changes.",
                isError: true,
            });
            return;
        }

        setBusyId(storeKey);

        setStatus({
            message: "",
            isError: false,
        });

        try {
            const changes = {};

            for (const field of EDITABLE_FIELDS) {
                const originalValue = original[field];
                const draftValue = draft[field];

                if (
                    JSON.stringify(originalValue) !==
                    JSON.stringify(draftValue)
                ) {
                    changes[field] = draftValue;
                }
            }

            if (Object.keys(changes).length === 0) {
                setStatus({
                    message: "No changes to save.",
                    isError: false,
                });

                setEditId(null);
                return;
            }

            const response = await axios.post(
                UPDATE_STORE_API,
                {
                    email: original.email,
                    changes,
                },
                {
                    timeout: 30000,
                }
            );

            const updatedStore =
                response.data?.store ||
                {
                    ...original,
                    ...changes,
                };

            setStores((previous) =>
                previous.map((store) =>
                    getStoreKey(store) === storeKey
                        ? updatedStore
                        : store
                )
            );

            setDrafts((previous) => ({
                ...previous,
                [storeKey]: cloneStore(updatedStore),
            }));

            setEditId(null);

            setStatus({
                message:
                    `Successfully updated "${updatedStore.name || original.name}".`,
                isError: false,
            });

        } catch (error) {
            console.error(
                "Failed to update Store:",
                error
            );

            setStatus({
                message: getErrorMessage(
                    error,
                    "Failed to update Store."
                ),
                isError: true,
            });

        } finally {
            setBusyId(null);
        }
    };

    /* =====================================================
       DELETE STORE
       ===================================================== */

    const handleDelete = async (storeKey) => {
        const store = stores.find(
            (item) => getStoreKey(item) === storeKey
        );

        if (!store) return;

        if (!store.email) {
            setStatus({
                message:
                    "Cannot delete Store without an email.",
                isError: true,
            });
            return;
        }

        const label =
            store.name ||
            store.email;

        const billingStatus =
            getBillingStatus(store);

        if (
            billingStatus === "active" ||
            billingStatus === "trialing" ||
            billingStatus === "past_due"
        ) {
            setStatus({
                message:
                    `Cannot delete "${label}" while its subscription is ` +
                    `${formatBillingStatus(billingStatus)}. ` +
                    `Resolve its Stripe subscription first.`,
                isError: true,
            });

            return;
        }

        const confirmed = window.confirm(
            `Permanently delete Store "${label}"?\n\n` +
            `Email: ${store.email}\n\n` +
            `This will remove the Store record and may remove ` +
            `associated Store data.\n\n` +
            `This action cannot be undone.`
        );

        if (!confirmed) return;

        setBusyId(storeKey);

        setStatus({
            message: "",
            isError: false,
        });

        try {
            await axios.post(
                DELETE_STORE_API,
                {
                    email: store.email,
                },
                {
                    timeout: 30000,
                }
            );

            setStores((previous) =>
                previous.filter(
                    (item) =>
                        getStoreKey(item) !== storeKey
                )
            );

            setDrafts((previous) => {
                const next = { ...previous };
                delete next[storeKey];
                return next;
            });

            setExpandedId(null);
            setEditId(null);

            setStatus({
                message:
                    `Store "${label}" deleted successfully.`,
                isError: false,
            });

        } catch (error) {
            console.error(
                "Failed to delete Store:",
                error
            );

            setStatus({
                message: getErrorMessage(
                    error,
                    "Failed to delete Store."
                ),
                isError: true,
            });

        } finally {
            setBusyId(null);
        }
    };

    /* =====================================================
       RENDER
       ===================================================== */

    return (
        <div style={styles.container}>

            {/* HEADER */}

            <div style={styles.headerRow}>
                <div>
                    <h2 style={styles.heading}>
                        Secondhand Stores
                    </h2>

                    <div style={styles.subheading}>
                        Search, view, edit, or remove approved Stores.
                    </div>
                </div>

                <button
                    className="btn-secondary"
                    style={{ height: 38 }}
                    onClick={fetchStores}
                    disabled={loading || !!busyId}
                >
                    {loading ? "Loading…" : "Refresh"}
                </button>
            </div>

            {/* SUMMARY */}

            <div style={styles.summaryRow}>
                <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>
                        Approved Stores
                    </div>

                    <div style={styles.summaryValue}>
                        {storeCounts.total}
                    </div>
                </div>

                <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>
                        Active Subscriptions
                    </div>

                    <div style={styles.summaryValue}>
                        {storeCounts.active}
                    </div>
                </div>

                <div style={styles.summaryCard}>
                    <div style={styles.summaryLabel}>
                        Trial Subscriptions
                    </div>

                    <div style={styles.summaryValue}>
                        {storeCounts.trial}
                    </div>
                </div>
            </div>

            {/* SEARCH */}

            <div style={styles.searchWrap}>
                <input
                    type="text"
                    placeholder="Search by Store name, ID, email, address…"
                    value={search}
                    onChange={(event) =>
                        setSearch(event.target.value)
                    }
                    style={styles.searchInput}
                />

                <div style={styles.searchMeta}>
                    Showing{" "}
                    <strong>{filteredStores.length}</strong>
                    {" "}of{" "}
                    <strong>{stores.length}</strong>
                </div>
            </div>

            {/* STATUS */}

            {status.message && (
                <div
                    className="card"
                    style={{
                        ...styles.statusCard,
                        borderColor: status.isError
                            ? "#ffcccc"
                            : "#d9f2e3",
                        background: status.isError
                            ? "#fff6f6"
                            : "#f4fff8",
                    }}
                >
                    <div
                        style={{
                            fontWeight: 800,
                            color: status.isError
                                ? "#a94442"
                                : "#1f5130",
                        }}
                    >
                        {status.message}
                    </div>
                </div>
            )}

            {/* STORE LIST */}

            {loading ? (
                <div
                    className="card"
                    style={styles.emptyCard}
                >
                    Loading Stores…
                </div>
            ) : filteredStores.length === 0 ? (
                <div
                    className="card"
                    style={styles.emptyCard}
                >
                    {search
                        ? "No matching Stores."
                        : "No approved Stores found."}
                </div>
            ) : (
                <div style={styles.list}>
                    {filteredStores.map((store) => {
                        const id = getStoreKey(store);

                        const open =
                            expandedId === id;

                        const editing =
                            editId === id;

                        const busy =
                            busyId === id;

                        const draft =
                            drafts[id] || store;

                        const billingStatus =
                            getBillingStatus(store);

                        const billingColors =
                            getBillingColor(billingStatus);

                        return (
                            <div
                                key={id}
                                className="card"
                                style={styles.card}
                            >

                                {/* COLLAPSED HEADER */}

                                <button
                                    type="button"
                                    onClick={() =>
                                        toggleExpand(id)
                                    }
                                    style={styles.cardTopButton}
                                    aria-expanded={open}
                                    disabled={!!busyId}
                                >
                                    <div style={styles.cardTopLeft}>

                                        <div style={styles.storeNameRow}>
                                            <div style={styles.storeName}>
                                                {store.name ||
                                                    "(Unnamed Store)"}
                                            </div>

                                            <span style={styles.pill}>
                                                {store.countryName ||
                                                    store.country ||
                                                    "Secondhand Store"}
                                            </span>

                                            <span
                                                style={{
                                                    ...styles.billingPill,
                                                    background:
                                                        billingColors.background,
                                                    color:
                                                        billingColors.color,
                                                    borderColor:
                                                        billingColors.border,
                                                }}
                                            >
                                                {formatBillingStatus(
                                                    billingStatus
                                                )}
                                            </span>
                                        </div>

                                        <div style={styles.storeMeta}>
                                            <span>
                                                <strong>ID:</strong>{" "}
                                                {store.storeId ||
                                                    store.uid ||
                                                    store.id ||
                                                    "—"}
                                            </span>

                                            {store.email && (
                                                <span>
                                                    • <strong>Email:</strong>{" "}
                                                    {store.email}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div style={styles.expandIcon}>
                                        {open ? "−" : "+"}
                                    </div>
                                </button>

                                {/* EXPANDED PANEL */}

                                {open && (
                                    <div style={styles.panel}>

                                        {!editing ? (
                                            <>

                                                {/* PROFILE DETAILS */}

                                                <div style={styles.detailGrid}>

                                                    <InfoRow
                                                        label="Store Name"
                                                        value={
                                                            store.name || "—"
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Email"
                                                        value={
                                                            store.email || "—"
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Address"
                                                        value={
                                                            store.addressFormatted ||
                                                            store.address ||
                                                            "—"
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Contact"
                                                        value={
                                                            store.contact || "—"
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Opening Hours"
                                                        value={
                                                            <HoursView
                                                                hours={store.hours}
                                                            />
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Items Offered"
                                                        value={
                                                            <ItemsView
                                                                items={store.itemsOffered}
                                                            />
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Coordinates"
                                                        value={
                                                            formatLocation(store)
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Image URL"
                                                        value={
                                                            store.imageURL
                                                                ? (
                                                                    <a
                                                                        href={store.imageURL}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                    >
                                                                        View Store Image
                                                                    </a>
                                                                )
                                                                : "—"
                                                        }
                                                    />

                                                </div>

                                                {/* SUBSCRIPTION DETAILS */}

                                                <div style={styles.sectionHeading}>
                                                    Subscription Information
                                                </div>

                                                <div style={styles.detailGrid}>

                                                    <InfoRow
                                                        label="Billing Status"
                                                        value={
                                                            formatBillingStatus(
                                                                billingStatus
                                                            )
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Subscribed"
                                                        value={
                                                            store.isSubscribed === true
                                                                ? "Yes"
                                                                : store.isSubscribed === false
                                                                    ? "No"
                                                                    : "Unknown"
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Trial Ends"
                                                        value={
                                                            formatDate(
                                                                store.billing?.trialEndsAt
                                                            )
                                                        }
                                                    />

                                                    <InfoRow
                                                        label="Subscription ID"
                                                        value={
                                                            store.billing?.subscriptionId ||
                                                            "—"
                                                        }
                                                    />

                                                </div>

                                                {/* ACTIONS */}

                                                <div style={styles.actionRow}>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() =>
                                                            startEdit(id)
                                                        }
                                                        disabled={!!busyId}
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        className="btn-danger"
                                                        onClick={() =>
                                                            handleDelete(id)
                                                        }
                                                        disabled={!!busyId}
                                                    >
                                                        {busy
                                                            ? "Working…"
                                                            : "Delete"}
                                                    </button>
                                                </div>

                                            </>
                                        ) : (
                                            <>

                                                {/* EDIT FORM */}

                                                <div style={styles.detailGrid}>

                                                    <Field
                                                        label="Store Name"
                                                        value={
                                                            draft.name || ""
                                                        }
                                                        onChange={(value) =>
                                                            updateDraftField(
                                                                id,
                                                                "name",
                                                                value
                                                            )
                                                        }
                                                        disabled={busy}
                                                    />

                                                    <ReadOnlyField
                                                        label="Email / Partition Key"
                                                        value={
                                                            draft.email || "—"
                                                        }
                                                    />

                                                    <ReadOnlyField
                                                        label="Address"
                                                        value={
                                                            draft.addressFormatted ||
                                                            draft.address ||
                                                            "—"
                                                        }
                                                        note="Address changes are disabled until coordinate geocoding is added."
                                                    />

                                                    <Field
                                                        label="Contact"
                                                        value={
                                                            draft.contact || ""
                                                        }
                                                        onChange={(value) =>
                                                            updateDraftField(
                                                                id,
                                                                "contact",
                                                                value
                                                            )
                                                        }
                                                        disabled={busy}
                                                    />

                                                    <Field
                                                        label="Image URL"
                                                        value={
                                                            draft.imageURL || ""
                                                        }
                                                        onChange={(value) =>
                                                            updateDraftField(
                                                                id,
                                                                "imageURL",
                                                                value
                                                            )
                                                        }
                                                        disabled={busy}
                                                    />

                                                    <div style={styles.field}>
                                                        <div style={styles.fieldLabel}>
                                                            Items Offered
                                                        </div>

                                                        <textarea
                                                            value={
                                                                Array.isArray(
                                                                    draft.itemsOffered
                                                                )
                                                                    ? draft.itemsOffered.join("\n")
                                                                    : ""
                                                            }
                                                            onChange={(event) =>
                                                                updateDraftField(
                                                                    id,
                                                                    "itemsOffered",
                                                                    event.target.value
                                                                        .split("\n")
                                                                        .map((item) =>
                                                                            item.trim()
                                                                        )
                                                                        .filter(Boolean)
                                                                )
                                                            }
                                                            disabled={busy}
                                                            rows={5}
                                                            placeholder={
                                                                "Clothing\nFurniture\nHomewares"
                                                            }
                                                            style={styles.textarea}
                                                        />

                                                        <div style={styles.fieldNote}>
                                                            Enter one item category per line.
                                                        </div>
                                                    </div>

                                                    {/* OPENING HOURS */}

                                                    <div style={styles.hoursBlock}>
                                                        <div style={styles.fieldLabel}>
                                                            Opening Hours
                                                        </div>

                                                        <div style={styles.hoursGrid}>
                                                            {DAYS.map((day) => (
                                                                <div
                                                                    key={day}
                                                                    style={styles.hourRow}
                                                                >
                                                                    <div style={styles.hourDay}>
                                                                        {day}
                                                                    </div>

                                                                    <input
                                                                        value={
                                                                            draft.hours?.[day] || ""
                                                                        }
                                                                        onChange={(event) =>
                                                                            updateDraftHour(
                                                                                id,
                                                                                day,
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        placeholder="e.g. 9am - 5pm"
                                                                        style={styles.hourInput}
                                                                        disabled={busy}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                </div>

                                                {/* EDIT ACTIONS */}

                                                <div style={styles.actionRow}>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={() =>
                                                            handleSave(id)
                                                        }
                                                        disabled={busy}
                                                    >
                                                        {busy
                                                            ? "Saving…"
                                                            : "Save Changes"}
                                                    </button>

                                                    <button
                                                        className="btn-secondary"
                                                        onClick={() =>
                                                            cancelEdit(id)
                                                        }
                                                        disabled={busy}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>

                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/* =========================================================
   REUSABLE COMPONENTS
   ========================================================= */

function InfoRow({ label, value }) {
    return (
        <div style={styles.infoRow}>
            <div style={styles.infoLabel}>
                {label}
            </div>

            <div style={styles.infoValue}>
                {value}
            </div>
        </div>
    );
}

function Field({
    label,
    value,
    onChange,
    disabled = false,
}) {
    return (
        <div style={styles.field}>
            <div style={styles.fieldLabel}>
                {label}
            </div>

            <input
                value={value}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                disabled={disabled}
                style={styles.input}
            />
        </div>
    );
}

function ReadOnlyField({
    label,
    value,
    note,
}) {
    return (
        <div style={styles.field}>
            <div style={styles.fieldLabel}>
                {label}
            </div>

            <div style={styles.readOnlyValue}>
                {value}
            </div>

            {note && (
                <div style={styles.fieldNote}>
                    {note}
                </div>
            )}
        </div>
    );
}

function HoursView({ hours }) {
    if (
        !hours ||
        typeof hours !== "object" ||
        Object.keys(hours).length === 0
    ) {
        return (
            <span style={styles.muted}>
                Not set
            </span>
        );
    }

    return (
        <div style={styles.hoursView}>
            {DAYS.map((day) => (
                <div
                    key={day}
                    style={styles.hoursViewRow}
                >
                    <div style={styles.hoursViewDay}>
                        {day}
                    </div>

                    <div>
                        {hours[day] || "—"}
                    </div>
                </div>
            ))}
        </div>
    );
}

function ItemsView({ items }) {
    if (
        !Array.isArray(items) ||
        items.length === 0
    ) {
        return (
            <span style={styles.muted}>
                Not set
            </span>
        );
    }

    return (
        <div style={styles.itemsList}>
            {items.map((item, index) => (
                <span
                    key={`${item}-${index}`}
                    style={styles.itemPill}
                >
                    {item}
                </span>
            ))}
        </div>
    );
}

/* =========================================================
   STYLES

   Mirrors the existing ShopManager styling.
   ========================================================= */

const styles = {
    container: {
        padding: 10,
        width: "100%",
        fontFamily: "Nunito, sans-serif",
    },

    headerRow: {
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 14,
        flexWrap: "wrap",
    },

    heading: {
        fontSize: 26,
        color: "#5c4a3b",
        margin: 0,
        letterSpacing: 0.4,
    },

    subheading: {
        marginTop: 4,
        color: "#857969",
        fontSize: 13,
    },

    summaryRow: {
        display: "grid",
        gridTemplateColumns:
            "repeat(auto-fit, minmax(170px, 1fr))",
        gap: 12,
        marginBottom: 16,
    },

    summaryCard: {
        background: "#fff",
        border: "1px solid #e7e0d6",
        borderRadius: 14,
        padding: 14,
    },

    summaryLabel: {
        fontSize: 12,
        color: "#857969",
        textTransform: "uppercase",
        letterSpacing: 0.4,
        fontWeight: 700,
    },

    summaryValue: {
        fontSize: 25,
        fontWeight: 900,
        color: "#5c4a3b",
        marginTop: 6,
    },

    searchWrap: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 14,
        flexWrap: "wrap",
    },

    searchInput: {
        flex: 1,
        minWidth: 250,
        padding: "10px 12px",
        borderRadius: 12,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        background: "#fff",
        boxSizing: "border-box",
    },

    searchMeta: {
        color: "#857969",
        fontSize: 13,
    },

    statusCard: {
        marginBottom: 14,
        padding: 14,
        border: "1px solid",
        borderRadius: 12,
    },

    emptyCard: {
        textAlign: "center",
        padding: 24,
    },

    list: {
        display: "grid",
        gap: 12,
    },

    card: {
        border: "1px solid #e7e0d6",
        borderRadius: 14,
        background: "#fff",
        overflow: "hidden",
    },

    cardTopButton: {
        width: "100%",
        border: "none",
        background: "transparent",
        textAlign: "left",
        padding: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        cursor: "pointer",
    },

    cardTopLeft: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
        minWidth: 0,
    },

    storeNameRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },

    storeName: {
        fontWeight: 900,
        color: "#3c3329",
        fontSize: 16,
    },

    pill: {
        fontSize: 12,
        padding: "3px 10px",
        borderRadius: 999,
        background: "#f5f0e1",
        color: "#6b5b3a",
        border: `1px solid ${OPPIES_BEIGE}`,
        fontWeight: 800,
    },

    billingPill: {
        fontSize: 12,
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        fontWeight: 800,
    },

    storeMeta: {
        fontSize: 13,
        color: "#6f6357",
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        overflowWrap: "anywhere",
    },

    expandIcon: {
        width: 34,
        height: 34,
        flexShrink: 0,
        borderRadius: 10,
        border: "1px solid #e7e0d6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: OPPIES_BEIGE,
        fontWeight: 900,
        fontSize: 18,
        background: "#fffaf2",
    },

    panel: {
        borderTop: "1px solid #eee7de",
        padding: 14,
        background: "#fffaf2",
        borderBottomLeftRadius: 14,
        borderBottomRightRadius: 14,
    },

    detailGrid: {
        display: "grid",
        gap: 12,
    },

    sectionHeading: {
        marginTop: 20,
        marginBottom: 12,
        fontSize: 16,
        fontWeight: 900,
        color: "#5c4a3b",
    },

    infoRow: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },

    infoLabel: {
        fontSize: 12,
        color: "#857969",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    infoValue: {
        marginTop: 6,
        color: "#3c3329",
        fontSize: 14,
        overflowWrap: "anywhere",
    },

    actionRow: {
        display: "flex",
        gap: 10,
        marginTop: 14,
        flexWrap: "wrap",
    },

    field: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },

    fieldLabel: {
        fontSize: 12,
        color: "#857969",
        textTransform: "uppercase",
        letterSpacing: 0.5,
    },

    input: {
        marginTop: 8,
        width: "100%",
        padding: "10px 10px",
        borderRadius: 10,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
    },

    textarea: {
        marginTop: 8,
        width: "100%",
        padding: 10,
        borderRadius: 10,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        fontFamily: "inherit",
        resize: "vertical",
        boxSizing: "border-box",
    },

    readOnlyValue: {
        marginTop: 8,
        padding: "10px 12px",
        background: "#f8f6f2",
        border: "1px solid #ece5dc",
        borderRadius: 10,
        color: "#665c50",
        fontSize: 14,
        overflowWrap: "anywhere",
    },

    fieldNote: {
        marginTop: 7,
        fontSize: 12,
        color: "#96897b",
    },

    hoursBlock: {
        padding: 12,
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #efe7dd",
    },

    hoursGrid: {
        marginTop: 10,
        display: "grid",
        gap: 8,
    },

    hourRow: {
        display: "grid",
        gridTemplateColumns:
            "minmax(85px, 110px) minmax(0, 1fr)",
        gap: 10,
        alignItems: "center",
    },

    hourDay: {
        fontSize: 13,
        color: "#3c3329",
        fontWeight: 800,
    },

    hourInput: {
        padding: "9px 10px",
        borderRadius: 10,
        border: "1px solid #e1d9cd",
        fontSize: 14,
        width: "100%",
        boxSizing: "border-box",
    },

    hoursView: {
        display: "grid",
        gap: 6,
    },

    hoursViewRow: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
    },

    hoursViewDay: {
        width: 90,
        fontWeight: 700,
    },

    muted: {
        fontStyle: "italic",
        opacity: 0.75,
    },

    itemsList: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
    },

    itemPill: {
        fontSize: 12,
        padding: "4px 9px",
        background: "#f5f0e1",
        borderRadius: 999,
        border: "1px solid #e1d6bc",
        color: "#6b5b3a",
    },
};
